import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import React from 'react'

import { classes } from '../utils'

const focusStyle =
  'outline-none focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-focus-outline focus:outline-focus-outline focus:outline-2 focus:outline-offset-[-2px]'

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center whitespace-nowrap rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
    'outline-none focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-focus-outline focus:outline-focus-outline focus:outline-2 focus:outline-offset-[-2px]',
  ],
  {
    variants: {
      variant: {
        default:
          'bg-background text-foreground-normal enabled:hover:bg-background-surface/70 border border-border-normal',
        primary:
          'bg-background-inverse text-foreground-on-inverse enabled:hover:bg-background-inverse/85 disabled:border-border-normal disabled:opacity-100 disabled:bg-background-surface-hovered disabled:text-foreground-disabled',
        secondary:
          'bg-background-surface enabled:hover:bg-background-surface/90 text-foreground text-foreground-normal border border-border-normal',
        critical:
          'bg-fill-critical text-foreground-on-fill hover:bg-fill-critical/90',
        ghost:
          'bg-transparent text-foreground-normal border-none hover:bg-background-surface/70 active:bg-background-surface/80 data-[state=open]:bg-background-surface/80',
        link: 'text-fill-info underline-offset-4 hover:underline',
        outline:
          'bg-transparent text-foreground-normal border border-border-normal hover:bg-background-surface/70',
      },
      size: {
        default: 'h-8 px-4',
        sm: 'h-8 px-3',
        xs: 'h-6 px-2',
        lg: 'h-10 px-8',
        icon: 'h-6 w-6',
        square: 'h-8 w-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        type="button"
        className={classes(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants, focusStyle }
